import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IHHOralNotification } from '@core/model';
import { OralNotificationTypes, ViewMode } from '@core/enumeration/notification-oral';
import { ConfirmationService, SelectItem } from 'primeng/api';
import moment from 'moment';
import { ArrayUtilities } from '@shared/utility';
import { AuthenticationService } from '@core/service';
import { isEqual } from "lodash-es";
import { FeatureFlags } from '@core/enumeration';
import { FeatureToggleService } from '@mynexus/mynexus-ui-lib';
import { SubSink } from 'subsink';
@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'app-oral-notifications-form',
    templateUrl: './oral-notifications-form.component.html',
    styleUrls: ['./oral-notifications-form.component.scss'],
    standalone: false
})
export class OralNotificationsFormComponent implements OnInit {
  @Input() mode: ViewMode = ViewMode.View;
  @Input() model: IHHOralNotification = null;
  @Output() cancel = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  oralNotificationTypes: SelectItem[] = [];
  oralNotificationSubTypes: SelectItem[] = [];
  outcomeDropDownList: SelectItem[] = [];
  private readonly subs = new SubSink();
  featureFlags = FeatureFlags;
  isServiceLineTabWidgetEnabled = false;

  readonly ViewMode = ViewMode;
  readonly oralNotificationBaseSubTypes: string[] = [
    'Downgrade Communicated',
    'Authorization Notification',
  ];

  formModel: FormGroup;

  get canModifyExtraFields(): boolean {
    return this.authenticationService.getEmail === this.model?.creatorEmail && this.createdInLast24Hours;
  }

  get createdInLast24Hours(): boolean {
    const createdDate = moment.utc(this.model.createdOn).local();
    const today = moment(new Date());
    const duration = moment.duration(today.diff(createdDate));
    return duration.asHours() < 24;
  }

  get isFormModified(): boolean {
    return this.canModifyExtraFields
      ? !isEqual(
        {
          notificationDatetime: this.formModel.value.notificationDatetime?.toISOString().split('.')[0],
          notificationNote: this.formModel.value.notificationNote,
          notificationSubType: this.formModel.value.notificationSubType,
          oralNotificationType: this.formModel.value.oralNotificationType,
          outcome: this.formModel.value.outcome,
        },
        {
          notificationDatetime: this.model.notificationDateTime,
          notificationNote: this.model.notificationNote,
          notificationSubType: this.model.notificationSubType,
          oralNotificationType: this.model.oralNotificationType,
          outcome: this.model.outcome,
        }
      )
      : !isEqual(
        {
          notificationSubType: this.formModel.value.notificationSubType,
          oralNotificationType: this.formModel.value.oralNotificationType,
        },
        {
          notificationSubType: this.model.notificationSubType,
          oralNotificationType: this.model.oralNotificationType,
        }
      );
  }

  constructor(
    private authenticationService: AuthenticationService,
    private featureToggleService: FeatureToggleService,
    private confirmationService: ConfirmationService,
  ) {
    this.oralNotificationTypes = ArrayUtilities.arrayToSelectItemArrayByItem([
      OralNotificationTypes.Member1,
      OralNotificationTypes.Member2,
      OralNotificationTypes.Member3,
      OralNotificationTypes.Provider1,
      OralNotificationTypes.Provider2,
      OralNotificationTypes.Provider3,
      OralNotificationTypes.ReferralSource1,
      OralNotificationTypes.ReferralSource2,
      OralNotificationTypes.ReferralSource3
    ]);
  }

  ngOnInit() {
    this.initForm();
    this.subs.sink = this.featureToggleService.getFeatureAccess(FeatureFlags.HHActiveServiceLineTabWidget)
      .subscribe(enabled => this.isServiceLineTabWidgetEnabled = enabled);
  }

  initForm() {
    const model = this.model || {} as IHHOralNotification;
    const date = model && model.notificationDateTime
      ? new Date(moment.parseZone(model.notificationDateTime).utc().format())
      : null;
    const oralNotificationType = model.oralNotificationType ? [model.oralNotificationType] : model.oralNotificationType;
    const isNotCreateMode = this.mode !== ViewMode.Create;

    this.formModel = new FormGroup({
      oralNotificationType: new FormControl(oralNotificationType, [Validators.required]),
      notificationSubType: new FormControl(model.notificationSubType, [Validators.required]),
      hcpcsDescription: new FormControl({ value: model.hcpcsDescription, disabled: true }, [Validators.required]),
      hcpcsId: new FormControl({ value: model.hcpcsId, disabled: isNotCreateMode }, [Validators.required]),
      notificationNote: new FormControl({ value: model.notificationNote, disabled: isNotCreateMode && !this.canModifyExtraFields }, [Validators.required]),
      notificationDatetime: new FormControl({ value: date, disabled: isNotCreateMode && !this.canModifyExtraFields }, [Validators.required]),
      outcome: new FormControl({ value: model.outcome, disabled: isNotCreateMode && !this.canModifyExtraFields }, [Validators.required])
    });
    this.initOralNotificationSubTypes([model.oralNotificationType]);

    if (this.mode === ViewMode.View) {
      this.formModel.disable();
    }
  }

  initOralNotificationSubTypes(value: string[]) {
    if (!value.includes(undefined)) {
      const containsMemberValue = this.containsValues(value,
        [OralNotificationTypes.Member1, OralNotificationTypes.Member2, OralNotificationTypes.Member3]);

      if (containsMemberValue) {
        this.oralNotificationSubTypes = ArrayUtilities.arrayToSelectItemArrayByItem(
          this.oralNotificationBaseSubTypes);
      }
      else {
        this.oralNotificationSubTypes = ArrayUtilities.arrayToSelectItemArrayByItem(
          this.oralNotificationBaseSubTypes.concat(['Request for Additional Info']));
      }
    }
  }

  loadSubTypesOnSelection(ev: { originalEvent: Event, value: string[] }) {
    this.formModel.get('notificationSubType').setValue('');
    this.initOralNotificationSubTypes(ev.value);
  }

  onCancel() {
    if (this.formModel?.dirty) {
      this.confirmationService.confirm({
        message: `You have unsaved changes that will be lost.
        Are you sure you want to continue?`,
        accept: () => {
          this.cancel.emit();
          this.confirmationService.close();
        },
        reject: () => this.confirmationService.close(),
        key: 'confirmDiscard',
        header: 'Unsaved Changes',
      });
    } else {
      this.cancel.emit();
    }
  }

  onSubmit() {
    this.save.emit({
      ...this.formModel.getRawValue()
    });
  }

  setOutcome(value: string) {
    this.formModel.get('outcome')?.setValue(value);
  }

  private containsValues(arr1: string[], arr2: string[]): boolean {
    return arr1.some(value => arr2.includes(value));
  }
}
